/**
 * Free gift at the $75 threshold.
 *
 * Rendering and eligibility live in snippets/cart-free-gift.liquid — this file only
 * mutates the cart and lets the theme re-render. Keeping the math server-side means
 * the module can never disagree with what Liquid decided.
 *
 * The gift line is marked with the hidden line item property `_free_gift`.
 * Pricing to $0 is handled by the "Free charm over $75" Shopify automatic discount.
 */
class MFreeGift extends HTMLElement {
  constructor() {
    super();

    this.rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.cartDrawer = document.querySelector('m-cart-drawer');
    this.isCartPage = document.body.classList.contains('template-cart');
    this.busy = false;

    this.giftLineKey = this.dataset.giftLineKey || '';

    this.addEventListener('click', this.onClick.bind(this));

    // Self-heal: the cart dropped back under the threshold but a gift is still in it.
    if (this.dataset.shouldRemove === 'true' && this.giftLineKey) {
      this.removeGift();
    }
  }

  onClick(event) {
    const addButton = event.target.closest('[data-free-gift-add]');
    if (addButton) {
      event.preventDefault();
      this.addGift();
      return;
    }

    const changeButton = event.target.closest('[data-free-gift-change]');
    if (changeButton) {
      event.preventDefault();
      this.removeGift();
    }
  }

  addGift() {
    const select = this.querySelector('[data-free-gift-select]');
    if (!select || !select.value || this.busy) return;

    this.toggleLoading(true);

    this.request(`${this.rootUrl}cart/add.js`, {
      items: [
        {
          id: Number(select.value),
          quantity: 1,
          properties: { _free_gift: 'true' },
        },
      ],
    });
  }

  removeGift() {
    if (!this.giftLineKey || this.busy) return;

    this.toggleLoading(true);

    this.request(`${this.rootUrl}cart/change.js`, {
      id: this.giftLineKey,
      quantity: 0,
    });
  }

  request(url, body) {
    this.busy = true;

    return fetch(url, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then(() => this.refreshCart())
      .catch(() => {
        this.busy = false;
        this.toggleLoading(false);
      });
  }

  /**
   * Mirrors gift-wrapping.js: full reload on the cart page, in-place refresh in the drawer.
   * Both paths re-render cart-free-gift.liquid, so state is always recomputed server-side.
   */
  refreshCart() {
    if (this.isCartPage || !this.cartDrawer) {
      window.location.href = (window.MinimogSettings && window.MinimogSettings.routes.cart) || `${this.rootUrl}cart`;
      return;
    }

    return fetch(`${this.rootUrl}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        this.cartDrawer.onCartDrawerUpdate();
        this.cartDrawer.classList.toggle('m-cart--empty', cart.item_count === 0);

        if (window.MinimogEvents && window.MinimogTheme) {
          MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, cart);
        }
      })
      .finally(() => {
        this.busy = false;
        this.toggleLoading(false);
      });
  }

  toggleLoading(state) {
    this.classList.toggle('m-free-gift--loading', Boolean(state));
  }
}

customElements.define('m-free-gift', MFreeGift);

/**
 * Keep the drawer copy in sync.
 *
 * m-cart-drawer's onCartDrawerUpdate() only re-renders [data-minimog-cart-items],
 * [data-minimog-cart-discounts], [data-cart-subtotal] and [data-minimog-gift-wrapping].
 * This module sits in the drawer header, which is never refreshed - so without this it
 * would show whatever state the page was loaded with. (The cart page is fine: mutations
 * there do a full reload.)
 *
 * Subscribed once globally rather than per-element, since refreshing replaces the
 * element and a per-element subscription would leak on every cart update.
 */
(function syncDrawerCopy() {
  if (window.__mFreeGiftDrawerSync) return;
  window.__mFreeGiftDrawerSync = true;

  const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let refreshing = false;

  /**
   * Put the shopper's charm choice back after the picker is re-rendered.
   *
   * Only restores a value the fresh markup actually offers, so a variant that sold out
   * between renders falls through to the server's default instead of a dead option.
   */
  function restoreChoice(root, value) {
    if (!value) return;

    const select = root.querySelector('[data-free-gift-select]');
    if (!select) return;

    if (Array.from(select.options).some((option) => option.value === value)) {
      select.value = value;
    }
  }

  async function refreshDrawerRoot() {
    const root = document.querySelector('m-cart-drawer [data-free-gift-root]');
    if (!root || refreshing) return;

    refreshing = true;
    try {
      const response = await fetch(`${rootUrl}?section_id=cart-drawer`);
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const fresh = parsed.querySelector('[data-free-gift-root]');

      /**
       * Read the choice AFTER the fetch resolves, not before it starts.
       *
       * The fetch takes a few hundred ms and shoppers pick inside that window. Without
       * this, any cart update between choosing a charm and pressing "Add my free charm"
       * reset the picker to its first option - and that first option is what shipped,
       * whatever they had actually selected.
       */
      const current = root.querySelector('[data-free-gift-select]');
      const chosen = current ? current.value : '';

      // innerHTML (not replaceWith) so the anchor survives and can repopulate later.
      root.innerHTML = fresh ? fresh.innerHTML : '';
      restoreChoice(root, chosen);
    } catch (error) {
      /* leave the stale render in place rather than blanking the module */
    } finally {
      refreshing = false;
    }
  }

  function subscribe() {
    if (!window.MinimogEvents || !window.MinimogTheme) return false;
    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, refreshDrawerRoot);
    return true;
  }

  if (!subscribe()) {
    document.addEventListener('DOMContentLoaded', subscribe, { once: true });
  }
})();
